import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, Library, ListChecks, Repeat, ShieldCheck,
  CreditCard, LogOut, Menu, Landmark, DatabaseZap, Briefcase, IndianRupee,
  Layers, ConciergeBell, UserPlus, PlugZap, KeyRound, IdCard, FlaskConical, AlertTriangle,
  Microscope,
  BookOpenCheck,
  FileCheck2,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { adminApi } from "@/lib/api";

// Nav grouped into plain-language sections. Each item has a one-line "what this does" so a
// non-technical operator never has to guess. Jargon is paired with everyday words.
const NAV_SECTIONS = [
  {
    section: "Connect to BSE",
    items: [
      { to: "/admin/bse2", label: "Exchange link", desc: "Check the live BSE connection and run test flows", icon: PlugZap },
      { to: "/admin/bse2/workbench", label: "API operations", desc: "Operate UCC, order, plan, mandate, payment and folio lifecycles", icon: FlaskConical },
      { to: "/admin/bse2/uat-dossier", label: "BSE UAT dossier", desc: "Endpoint mapping, UAT evidence and downloadable go-live PDF", icon: FileCheck2 },
      { to: "/admin/ucc-onboarding", label: "Register investors", desc: "Create investor accounts (UCC) on BSE", icon: IdCard },
      { to: "/admin/bse-reconciliation", label: "Payment reconciliation", desc: "BSE member-PG lookup and Payment Aggregator MIS", icon: CreditCard },
      { to: "/admin/bse-credentials", label: "BSE keys & environment", desc: "Member login, UAT/Live switch, encryption keys", icon: KeyRound, superOnly: true },
    ],
  },
  {
    section: "Money movement",
    items: [
      { to: "/admin/orders", label: "Orders", desc: "Buy, sell and switch orders for all clients", icon: ListChecks },
      { to: "/admin/sips", label: "Recurring plans", desc: "SIP, STP and SWP systematic plans", icon: Repeat },
      { to: "/admin/mandates", label: "Auto-debit mandates", desc: "e-NACH / UPI mandates that let BSE debit clients", icon: ShieldCheck },
      { to: "/admin/payments", label: "Payments", desc: "Money collected against orders", icon: CreditCard },
    ],
  },
  {
    section: "Clients & data",
    items: [
      { to: "/admin/clients", label: "Client approvals", desc: "Approve KYC and send onboarding to BSE", icon: Users },
      { to: "/admin/users", label: "Login accounts", desc: "Ops and client login accounts", icon: UserPlus },
      { to: "/admin/schemes", label: "Fund list", desc: "Searchable list of every mutual-fund scheme", icon: Library },
      { to: "/admin/rta", label: "ARN data hub", desc: "CAMS, KFintech, folios, commissions and BSE UCC mapping", icon: DatabaseZap },
      { to: "/admin/research-ops", label: "Research operations", desc: "Sources, quality gates, jobs and publication lineage", icon: Microscope },
    ],
  },
  {
    section: "Business",
    items: [
      { to: "/admin/operations-guide", label: "Operations guide", desc: "Setup, daily runbooks, safety checks and downloadable PDF", icon: BookOpenCheck },
      { to: "/admin", label: "Dashboard", desc: "Key numbers at a glance", icon: LayoutDashboard },
      { to: "/admin/crm", label: "Advisor desk", desc: "Relationship-manager tools", icon: Briefcase },
      { to: "/admin/revenue", label: "Revenue", desc: "Brokerage and commissions", icon: IndianRupee },
      { to: "/admin/alternates", label: "AIF & PMS", desc: "Alternative investments", icon: Layers },
      { to: "/admin/servicing", label: "Client servicing", desc: "Statements, requests and documents", icon: ConciergeBell },
    ],
  },
];

const BSE_OPERATOR_SECTIONS = [
  {
    section: "Run BSE StAR UAT",
    items: [
      { to: "/admin/bse2", label: "Connection & certification", desc: "Check BSE connectivity and run the automated evidence suite", icon: PlugZap },
      { to: "/admin/bse2/workbench", label: "API operations", desc: "Use every read, update, cancel, auth and folio-change lifecycle", icon: FlaskConical },
      { to: "/admin/ucc-onboarding", label: "Investor registration", desc: "Create, update and deactivate BSE UCC test records", icon: IdCard },
      { to: "/admin/bse-reconciliation", label: "Payment & MIS tests", desc: "Exercise member-PG lookup and PA MIS flows", icon: CreditCard },
      { to: "/admin/bse2/uat-dossier", label: "UAT evidence", desc: "Endpoint mapping, results and downloadable certification PDF", icon: FileCheck2 },
    ],
  },
  {
    section: "Create & inspect transactions",
    items: [
      { to: "/admin/orders", label: "Buy / Purchase", desc: "Create new investment orders", icon: ListChecks },
      { to: "/admin/redemptions", label: "Sell / Redeem", desc: "Redeem by amount, units or all units", icon: ListChecks },
      { to: "/admin/switches", label: "Switch", desc: "Move holdings between schemes", icon: Repeat },
      { to: "/admin/sips", label: "Recurring plans", desc: "Register SIP, X-SIP, STP and SWP plans", icon: Repeat },
      { to: "/admin/mandates", label: "Auto-debit mandates", desc: "Register e-NACH, NACH and UPI Autopay", icon: ShieldCheck },
      { to: "/admin/payments", label: "Payments", desc: "Select authenticated orders and send payment info", icon: CreditCard },
    ],
  },
];

function SidebarContent({ pathname, onNavigate }) {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const operator = admin?.role === "bse_uat_operator";
  const sections = operator ? BSE_OPERATOR_SECTIONS : NAV_SECTIONS;
  const home = operator ? "/admin/bse2" : "/admin";
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-6">
        <Link to={home} className="flex items-center gap-2" onClick={onNavigate}>
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--brand)]">
            <Landmark className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading text-lg leading-none text-[var(--brand)]">{operator ? "BSE StAR UAT" : "SMIFS Ops"}</div>
            <div className="text-[11px] tracking-wide text-[var(--ink-muted)]">{operator ? "Hands-on API testing" : "Admin Dashboard"}</div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-4">
        {sections.map((group) => {
          const items = group.items.filter((it) => !it.superOnly || admin?.role === "super_admin");
          if (!items.length) return null;
          return (
            <div key={group.section}>
              <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--ink-muted)]/70">
                {group.section}
              </div>
              <div className="space-y-0.5">
                {items.map((item) => {
                  const active = pathname === item.to;
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={onNavigate}
                      title={item.desc}
                      data-testid={`admin-nav-${item.label.toLowerCase().replace(/[\s/()]+/g, "-").replace(/-+$/,"")}`}
                      className={`group flex items-start gap-3 rounded-xl px-3 py-2 transition-colors ${
                        active ? "bg-[var(--surface-2)] text-[var(--brand)]" : "text-[var(--ink)] hover:bg-[var(--surface-2)]"
                      }`}
                    >
                      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${active ? "text-[var(--cta)]" : "text-[var(--ink-muted)]"}`} />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium leading-tight">{item.label}</span>
                        <span className="block truncate text-[11px] leading-tight text-[var(--ink-muted)]">{item.desc}</span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="border-t border-[var(--glass-border)] p-4">
        <div className="mb-2 truncate text-sm font-medium text-[var(--ink)]">{admin?.full_name}</div>
        <div className="mb-3 truncate text-xs text-[var(--ink-muted)]">{admin?.email}</div>
        <Button
          variant="outline"
          size="sm"
          className="w-full rounded-full"
          data-testid="admin-logout-button"
          onClick={() => { logout(); navigate("/admin/login"); }}
        >
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </Button>
      </div>
    </div>
  );
}

// Prominent, unmissable banner: which BSE environment is this whole system talking to right now.
function EnvironmentBanner() {
  const [env, setEnv] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let ok = true;
    adminApi.get("/admin/bse2/environment", { showErrorToast: false })
      .then((r) => { if (ok) { setEnv(r.data); setFailed(false); } })
      .catch(() => ok && setFailed(true));
    return () => { ok = false; };
  }, []);
  if (failed) return <div className="truncate rounded-full border border-red-300 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700"><span className="sm:hidden">BSE unavailable</span><span className="hidden sm:inline">BSE environment unavailable</span></div>;
  if (!env) return <div className="truncate text-xs text-[var(--ink-muted)]"><span className="sm:hidden">Checking…</span><span className="hidden sm:inline">Checking environment…</span></div>;
  const live = env.is_live;
  return (
    <div
      data-testid="env-banner"
      title={`Talking to ${env.base_url} · data stored in ${env.data_store}`}
      className={`flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold sm:gap-2 sm:px-3 ${
        live
          ? "border-red-300 bg-red-50 text-red-700"
          : "border-amber-300 bg-amber-50 text-amber-800"
      }`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${live ? "bg-red-500" : "bg-amber-500"}`} />
      {live ? <AlertTriangle className="h-3.5 w-3.5" /> : <FlaskConical className="h-3.5 w-3.5" />}
      <span className="whitespace-nowrap sm:hidden">{live ? "LIVE" : "UAT"}</span>
      <span className="hidden whitespace-nowrap sm:inline">{live ? "LIVE — real money" : "UAT — testing only"}</span>
      <span className="hidden font-normal text-[var(--ink-muted)] sm:inline">· member {env.member_code || "—"}</span>
    </div>
  );
}

export function AdminLayout({ children }) {
  const location = useLocation();
  const { admin } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const operator = admin?.role === "bse_uat_operator";

  return (
    <div className="min-h-dvh smifs-page-wash">
      <div className="flex">
        <aside className="hidden w-[260px] shrink-0 border-r border-[var(--glass-border)] bg-white/70 backdrop-blur-md lg:block">
          <div className="sticky top-0 h-dvh">
            <SidebarContent pathname={location.pathname} />
          </div>
        </aside>

        <div className="flex min-h-dvh min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 flex min-w-0 items-center justify-between gap-2 border-b border-[var(--glass-border)] bg-white/80 px-3 py-2 backdrop-blur-md sm:px-4 sm:py-3 lg:px-8">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="admin-mobile-nav-trigger">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[88vw] max-w-[320px] p-0">
                  <SidebarContent pathname={location.pathname} onNavigate={() => setOpen(false)} />
                </SheetContent>
              </Sheet>
              <span className="font-heading text-base text-[var(--brand)] lg:hidden">{operator ? "BSE StAR UAT" : "SMIFS Ops"}</span>
            </div>
            <EnvironmentBanner />
          </header>
          <main className="min-w-0 flex-1 px-3 py-4 sm:px-4 sm:py-6 lg:px-8 lg:py-8">
            <div className="mx-auto min-w-0 w-full max-w-[1440px]">{children}</div>
          </main>
        </div>
      </div>
    </div>
  );
}
